import React, { useState, useEffect } from "react";
import {Image as ImageIcon, Calendar, Upload, Video, X} from 'lucide-react';
import { lumi } from '../lib/lumi';

function Gallery() {
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [galleryItems, setGalleryItems] = useState<any[]>([]);
  const [isAdmin, setIsAdmin] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploading, setUploading] = useState(false);

  const categories = ["all", "reunions", "events", "school", "projects"];

  useEffect(() => {
    loadGalleryItems();
    checkUserRole();
  }, []);

  async function checkUserRole() {
    const user = lumi.auth.user;
    setIsAdmin(user?.userRole === "ADMIN");
  }

  async function loadGalleryItems() {
    try {
      const result = await lumi.entities.gallery.list({
        sort: { createdAt: -1 },
        limit: 100
      });
      setGalleryItems(result?.list || []);
    } catch (error) {
      console.error("Failed to load gallery:", error);
    }
  }

  async function handleUpload(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    const files = formData.getAll("files") as File[];
    const title = formData.get("title") as string;
    const category = formData.get("category") as string;
    const description = formData.get("description") as string;

    if (!files.length || !title || !category) {
      alert("Please fill all required fields");
      return;
    }

    setUploading(true);
    try {
      const uploadResults = await lumi.tools.file.upload(files);
      
      for (const result of uploadResults) {
        if (result.fileUrl) {
          const mediaType = result.fileName.match(/\.(mp4|mov|avi|mkv|webm)$/i) ? "video" : "image";
          await lumi.entities.gallery.create({
            title,
            category,
            description,
            mediaUrl: result.fileUrl,
            mediaType,
            uploadedBy: lumi.auth.user?.userName || "Admin",
            createdAt: new Date()
          });
        }
      }

      alert("Upload successful!");
      setShowUploadModal(false);
      loadGalleryItems();
    } catch (error) {
      console.error("Upload failed:", error);
      alert("Upload failed. Please try again.");
    } finally {
      setUploading(false);
    }
  }

  const filteredItems = selectedCategory === "all" ?
  galleryItems :
  galleryItems.filter((item) => item.category === selectedCategory);

  return (
    <div className="min-h-screen py-16 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Page Header */}
        <div className="text-center mb-12">
          <h1 className="text-5xl font-bold mb-4 bg-gradient-to-r from-white to-[#DC143C] bg-clip-text text-[#B91C1C]">Gallery

          </h1>
          <p className="text-gray-400 text-lg">Capturing memories, celebrating moments</p>
          
          {isAdmin && (
            <button
              onClick={() => setShowUploadModal(true)}
              className="mt-6 px-6 py-3 bg-[#DC143C] hover:bg-red-600 text-white rounded-lg font-semibold transition-all flex items-center gap-2 mx-auto">
              <Upload size={20} />
              Upload Media
            </button>
          )}
        </div>

        {/* Category Filter */}
        <div className="flex flex-wrap justify-center gap-3 mb-12">
          {categories.map((category) =>
          <button
            key={category}
            onClick={() => setSelectedCategory(category)}
            className={`px-6 py-2 rounded-lg font-semibold capitalize transition-all ${
            selectedCategory === category ?
            "bg-[#DC143C] text-white" :
            "bg-[#1A1A1A] text-gray-300 hover:bg-[#2A2A2A]"}`
            }>

              {category}
            </button>
          )}
        </div>

        {/* Gallery Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredItems.map((item) =>
          <GalleryCard key={item._id} item={item} />
          )}
        </div>

        {galleryItems.length === 0 && (
          <div className="mt-16 text-center bg-gradient-to-br from-[#1A1A1A] to-[#0A0A0A] border border-[#DC143C]/30 rounded-2xl p-12">
            <ImageIcon size={48} className="text-[#DC143C] mx-auto mb-4" />
            <h3 className="text-2xl font-bold mb-3">No Media Yet</h3>
            <p className="text-gray-400 max-w-xl mx-auto">
              {isAdmin ? "Click 'Upload Media' to add photos and videos" : "Check back soon for updates"}
            </p>
          </div>
        )}
      </div>

      {/* Upload Modal */}
      {showUploadModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-gradient-to-br from-[#1A1A1A] to-[#0A0A0A] border border-[#DC143C]/30 rounded-2xl p-8 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold">Upload Media</h2>
              <button onClick={() => setShowUploadModal(false)} className="text-gray-400 hover:text-white">
                <X size={24} />
              </button>
            </div>

            <form onSubmit={handleUpload} className="space-y-6">
              <div>
                <label className="block text-sm font-semibold mb-2">Title *</label>
                <input
                  type="text"
                  name="title"
                  required
                  className="w-full px-4 py-3 bg-[#0A0A0A] border border-[#DC143C]/30 rounded-lg focus:border-[#DC143C] focus:outline-none"
                  placeholder="Event name or title"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold mb-2">Category *</label>
                <select
                  name="category"
                  required
                  className="w-full px-4 py-3 bg-[#0A0A0A] border border-[#DC143C]/30 rounded-lg focus:border-[#DC143C] focus:outline-none">
                  <option value="">Select category</option>
                  {categories.filter(c => c !== "all").map(cat => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-semibold mb-2">Description</label>
                <textarea
                  name="description"
                  rows={3}
                  className="w-full px-4 py-3 bg-[#0A0A0A] border border-[#DC143C]/30 rounded-lg focus:border-[#DC143C] focus:outline-none"
                  placeholder="Brief description"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold mb-2">Files (Photos/Videos) *</label>
                <input
                  type="file"
                  name="files"
                  multiple
                  required
                  accept="image/*,video/*"
                  className="w-full px-4 py-3 bg-[#0A0A0A] border border-[#DC143C]/30 rounded-lg focus:border-[#DC143C] focus:outline-none"
                />
                <p className="text-gray-500 text-sm mt-2">Supports: JPG, PNG, MP4, MOV</p>
              </div>

              <div className="flex gap-4">
                <button
                  type="button"
                  onClick={() => setShowUploadModal(false)}
                  className="flex-1 px-6 py-3 bg-[#1A1A1A] hover:bg-[#2A2A2A] rounded-lg font-semibold transition-all">
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={uploading}
                  className="flex-1 px-6 py-3 bg-[#DC143C] hover:bg-red-600 rounded-lg font-semibold transition-all disabled:opacity-50">
                  {uploading ? "Uploading..." : "Upload"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>);

}

function GalleryCard({ item }: any) {
  return (
    <div className="group bg-gradient-to-br from-[#1A1A1A] to-[#0A0A0A] border border-[#DC143C]/30 rounded-xl overflow-hidden hover:border-[#DC143C] transition-all transform hover:scale-105 shadow-lg hover:shadow-2xl hover:shadow-[#DC143C]/20">
      {/* Media Display */}
      <div className="relative h-64 bg-gradient-to-br from-[#2A2A2A] to-[#1A1A1A] flex items-center justify-center overflow-hidden">
        {item.mediaType === "video" ? (
          <video
            src={item.mediaUrl}
            controls
            className="w-full h-full object-cover"
          />
        ) : (
          <img
            src={item.mediaUrl}
            alt={item.title}
            className="w-full h-full object-cover"
          />
        )}
        <div className="absolute top-4 right-4 px-3 py-1 bg-[#DC143C] rounded-full text-xs font-semibold">
          {item.category}
        </div>
      </div>

      {/* Card Content */}
      <div className="p-6">
        <div className="flex items-center text-gray-400 text-sm mb-2">
          <Calendar size={16} className="mr-2" />
          {item.createdAt ? new Date(item.createdAt).toLocaleDateString() : "Recent"}
        </div>
        <h3 className="text-xl font-bold mb-2 text-white group-hover:text-[#DC143C] transition-colors">
          {item.title}
        </h3>
        {item.description && <p className="text-gray-400 text-sm">{item.description}</p>}
      </div>
    </div>);

}

export default Gallery;